import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    Injector,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentPdfController } from 'src/app/domain/controllers/document/document-pdf.controller';
import { ActivatedRoute, Router } from '@angular/router';
import {
    DocGenPdfStatusEnum,
    DocumentEntity,
    DocumentItemTypeEnum,
    DocumentStatusEnum,
    DocumentTypeEnum,
    SignedByPartial,
} from '@domain';
import { environment } from '@environments';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaButtonComponent, RaToastService } from '@shared/components';
import { RaImagesFullScreenComponent } from 'ra-ui';
import { switchMap } from 'rxjs';
import { CoverTemplateController } from 'src/app/domain/controllers/cover-template.controller';
import { DocumentContractController } from 'src/app/domain/controllers/document/document-contract.controller';
import { previewTokenStorageKey } from 'src/app/domain/APIs/documents/document-preview.api';
import { DocumentPreviewController } from 'src/app/domain/controllers/document/document-preview.controller';
import { CoverTemplateEntity } from 'src/app/domain/entities/partials/cover-template/cover-template.entity';
import { DocumentSignedStatusEnum } from 'src/app/domain/enums/documents/document-signed-status.enum';
import { RaEmptyComponent } from 'src/app/shared/components/ra-empty/ra-empty.component';
import { RaViewerTextComponent } from 'src/app/shared/components/ra-viewer-text/ra-viewer-text.component';
import { resolveCalloutColor, resolveCalloutStyle } from '../../callout-colors';
import { StatsGridComponent } from '../../components/stats-grid/stats-grid.component';
import { TableViewComponent } from '../../components/table-view/table-view.component';
import { SemaphoreViewComponent } from '../../components/semaphore-view/semaphore-view.component';
import { ChartViewComponent } from '../../components/chart-view/chart-view.component';
import {
    downloadFileFromUrl,
    openFileInNewTab,
    pdfFileNameFromTitle,
} from 'src/app/shared/utils/file-download.util';
import { UtilService } from 'src/app/shared/utils/utils.service';
import { CoverModule } from './components/covers/cover.module';
import { ImageDataComponent } from './components/image-data/image-data.component';
import { ModalConfirmCodeComponent } from './components/modal-confirm-code/modal-confirm-code.component';
import { QuoteComponent } from './components/quote/quote.component';
import { SignatureDataComponent } from './components/signature-data/signature-data.component';
import { SignatureFlowComponent } from './components/signature-flow/signature-flow.component';
import { SummaryComponent } from './components/summary/summary.component';
import { VideoDataComponent } from './components/video-data/video-data.component';
import { buildPreviewGallery, PreviewGallery, previewGalleryKey } from './preview-gallery.util';
import { itemTextColor, itemTextSizeRem } from '../../text-style-options';

@Component({
    selector: 'document-preview',
    templateUrl: './document-preview.html',
    styleUrl: './document-preview.scss',
    imports: [
        CommonModule,
        RaEmptyComponent,
        RaButtonComponent,
        CoverModule,
        SummaryComponent,
        ImageDataComponent,
        SignatureDataComponent,
        VideoDataComponent,
        StatsGridComponent,
        TableViewComponent,
        SemaphoreViewComponent,
        ChartViewComponent,
        ModalConfirmCodeComponent,
        SignatureFlowComponent,
        QuoteComponent,
        RaViewerTextComponent,
        RaImagesFullScreenComponent,
        TranslatePipe,
    ],
})
export class DocumentPreviewPage implements OnInit {
    public loading = signal(true);
    public error = signal(false);
    public hideMenu = signal(false);
    public showModalConfirmCode = signal(false);
    public showResendSection = signal(false);
    public resendCodeLoading = signal(false);
    public confirmCodeLoading = signal(false);
    public signatureFlowOpen = signal(false);
    public document = signal<DocumentEntity | null>(null);
    /** Tamanho próprio do título/subtítulo/texto (var `--item-size`), ou null = padrão. */
    public itemTextSize(
        item: { configs?: { textSize?: number | null } } | null | undefined
    ): string | null {
        return itemTextSizeRem(item?.configs);
    }

    /** Cor própria do título/subtítulo/texto (var `--item-color`), ou null = padrão. */
    public itemTextColor(
        item: { configs?: { textColor?: string | null } } | null | undefined
    ): string | null {
        return itemTextColor(item?.configs);
    }

    /** Espaçamento entre linhas (var CSS `--line-spacing`); 1.5 = default do PDF. */
    public lineSpacing = computed(() => this.document()?.config?.lineSpacing ?? 1.5);
    /** Tamanho do texto normal (var CSS `--text-size`, pt => rem: pt ÷ 12); 12pt = 1rem = default do PDF. */
    public textSize = computed(() => {
        const pt = this.document()?.config?.fontSize ?? 12;
        return `${Math.round((pt / 12) * 10000) / 10000}rem`;
    });
    /**
     * Config `sectionPageBreak: false`: as seções deixam de ser blocos separados e
     * fluem como uma folha só, espelhando o PDF (`.container-section.section-flow`).
     */
    public sectionsFlow = computed(() => this.document()?.config?.sectionPageBreak === false);
    public coverTemplate = signal<CoverTemplateEntity | null>(null);
    public signedBy = signal<SignedByPartial | null>(null);
    public galleryOpen = signal(false);
    public galleryIndex = signal(0);
    public DocumentTypeEnum = DocumentTypeEnum;
    public DocumentItemTypeEnum = DocumentItemTypeEnum;

    /** Família de cor da caixa de destaque (ausente => azul, como no PDF). */
    public readonly resolveCalloutColor = resolveCalloutColor;
    public readonly resolveCalloutStyle = resolveCalloutStyle;
    public DocGenPdfStatusEnum = DocGenPdfStatusEnum;
    public DocumentSignedStatusEnum = DocumentSignedStatusEnum;

    // * [Injects]
    private _utilService = inject(UtilService);
    private _toastService = inject(RaToastService);
    private _router = inject(Router);
    private _activateRoute = inject(ActivatedRoute);
    private _coverTemplateController = inject(CoverTemplateController);
    private _documentPreviewController = inject(DocumentPreviewController);
    private _documentContractController = inject(DocumentContractController);
    private _injector = inject(Injector);
    private _sanitizer = inject(DomSanitizer);
    private _documentPdfController = inject(DocumentPdfController);
    private _destroyRef = inject(DestroyRef);

    /**
     * Só mostra o aviso de "estamos preparando" depois disto. Em conexão boa a folha fica
     * pronta antes, e um modal que aparece e some no mesmo segundo é pior que modal nenhum.
     */
    private static readonly PRINT_OVERLAY_DELAY_MS = 5000;

    /**
     * Teto da espera. A folha tem os próprios timeouts, mas eles vivem DENTRO do iframe: se
     * ela morrer antes de montar (sem saldo, sem rede), nenhuma mensagem chega aqui e o
     * usuário ficaria preso na tela de espera.
     */
    private static readonly PRINT_TIMEOUT_MS = 45000;

    private readonly _printFrameEl = viewChild<ElementRef<HTMLIFrameElement>>('printFrame');

    /** Folha do PDF em impressão, escondida (ver `requestPDF`). */
    public printFrameUrl = signal<SafeResourceUrl | null>(null);
    public preparingPrint = signal(false);
    /** Aviso em tela cheia — só aparece na espera longa (ver PRINT_OVERLAY_DELAY_MS). */
    public printOverlayOpen = signal(false);

    private _printOverlayTimer: ReturnType<typeof setTimeout> | null = null;
    private _printTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Documento já cobrado nesta tela. Cancelar e tentar de novo NÃO cobra outra vez: a
     * primeira cobrança pagou por um PDF que o usuário não chegou a receber, e cobrar de
     * novo por causa de uma espera longa é culpar o cliente pela nossa lentidão.
     *
     * Vive na tela, não no servidor: sair e voltar é um novo pedido, e cobra.
     */
    private _chargedDocumentId: string | null = null;

    ngOnInit(): void {
        this._checkDocumentAccess();
        this._checkIfShowMenu();

        window.addEventListener('message', this._onPrintFrameMessage);
        this._destroyRef.onDestroy(() => {
            window.removeEventListener('message', this._onPrintFrameMessage);
            this._clearPrintTimers();
        });
    }

    //#region [Galeria em tela cheia]

    /** Todas as fotos do documento numa lista única (ver `buildPreviewGallery`). */
    public gallery = computed<PreviewGallery>(() =>
        buildPreviewGallery(
            this.document()?.sessions,
            this._injector.get(TranslateService).instant('document.edit.imageCard.defaultAlt')
        )
    );

    /** Marca d'água da galeria: mesma regra do card (config ligada + company com logo). */
    public galleryWatermark = computed<string | undefined>(() => {
        const logo = this.document()?.company?.logo;
        return this.document()?.config?.showImageWatermark && logo ? logo : undefined;
    });

    /**
     * Abre a galeria na foto clicada. O `image-data` sabe apenas o índice da foto
     * DENTRO dele; a conversão para o índice no documento é feita aqui, que é quem
     * conhece a ordem das seções.
     */
    public openGallery(sectionIndex: number, itemIndex: number, imageIndex: number): void {
        const offset = this.gallery().offsets.get(previewGalleryKey(sectionIndex, itemIndex));
        if (offset === undefined) return;

        this.galleryIndex.set(offset + imageIndex);
        this.galleryOpen.set(true);
    }

    //#endregion

    //#region [Get preview data]

    private _goToNotFound(): void {
        this.loading.set(false);
        void this._router.navigate(['/not-found']);
    }

    private _checkDocumentAccess(): void {
        const id = this._activateRoute.snapshot.params['id'];
        if (!id) {
            this._goToNotFound();
            return;
        }

        // O webview do app abre o preview com `?previewToken=` (o usuário já está logado no
        // app, mas a web dentro do webview não tem sessão). Guardamos o token para que a
        // camada de API o envie no header `preview-token` — quem decide o acesso é o backend.
        //
        // Isto substitui o antigo `insideRapidocsCode`: um segredo estático, IDÊNTICO em
        // dev/hom/prod, embutido no bundle JS público e no APK, que fazia o FRONT pular o gate
        // de acesso. Qualquer um que lesse o bundle abria documento privado de qualquer empresa.
        const previewToken = this._activateRoute.snapshot.queryParams['previewToken'];
        if (previewToken) sessionStorage.setItem(previewTokenStorageKey(id), previewToken);

        // Verifica se o documento tem acesso
        this._documentPreviewController.checkDocumentAccess(id).subscribe({
            next: (response: string) => {
                switch (response) {
                    case 'HAS_ACCESS':
                        this.getPreviewData();
                        break;
                    case 'NEED_CODE':
                        const isDocumentVerified = localStorage.getItem(`contract_token_${id}`);
                        if (isDocumentVerified) {
                            this.getPreviewData();
                            this.getSignedStatus();
                            return;
                        }

                        this.loading.set(false);
                        this.showModalConfirmCode.set(true);
                        break;
                    default:
                        this._goToNotFound();
                        break;
                }
            },
            error: () => this._goToNotFound(),
        });
    }

    public getPreviewData(): void {
        // Setando os estados iniciais
        this.loading.set(true);
        this.error.set(false);

        const id = this._activateRoute.snapshot.params['id'];
        if (!id) {
            this._goToNotFound();
            return;
        }

        // Buscando os dados do documento
        this._documentPreviewController
            .get(id)
            .pipe(
                switchMap((data: DocumentEntity) => {
                    this.document.set(data);

                    return this._coverTemplateController.getById(data._id);
                })
            )
            .subscribe({
                next: data => {
                    this.coverTemplate.set(data);
                    this.loading.set(false);
                },
                error: () => this._goToNotFound(),
            });
    }

    //#endregion

    //#region [Code verification]

    public onCodeConfirm(code: string): void {
        const id = this._activateRoute.snapshot.params['id'];
        this.confirmCodeLoading.set(true);
        this._documentContractController.verifyCode(id, code).subscribe({
            next: () => {
                this.showModalConfirmCode.set(false);
                this.getPreviewData();
                this.getSignedStatus();
                this.confirmCodeLoading.set(false);
            },
            error: () => {
                this.confirmCodeLoading.set(false);
                this.showResendSection.set(true);
            },
        });
    }

    public onResendCode(email: string): void {
        this.resendCodeLoading.set(true);

        const id = this._activateRoute.snapshot.params['id'];
        this._documentContractController.resendCode(id, email).subscribe({
            next: () => this.resendCodeLoading.set(false),
        });
    }

    public getSignedStatus(): void {
        const id = this._activateRoute.snapshot.params['id'];

        this._documentContractController.verifySigned(id).subscribe({
            next: (response: SignedByPartial) => this.signedBy.set(response),
            error: () => this.signedBy.set(null),
        });
    }

    //#endregion

    //#region [PDF methods]

    /**
     * Gera o PDF: cobra, monta a folha paginada num iframe escondido e manda imprimir
     * quando ela avisa que terminou. Não há render no servidor nem arquivo para baixar
     * depois; o PDF é esta folha passando pelo "Salvar como PDF" do navegador.
     *
     * Num iframe, e não nesta página, porque aqui é o documento em LEITURA: imprimir daqui
     * sairia com o menu, o cabeçalho do RapiDocs e sem paginação nenhuma.
     */
    public requestPDF(): void {
        const id = this.document()?._id;
        if (!id || this.preparingPrint()) return;

        this.preparingPrint.set(true);

        // Cobra ANTES de montar a folha: ela sai impressa do navegador e some do nosso
        // alcance, então não há como cobrar depois. Sem saldo a API responde 402, o
        // controller mostra o toast e a folha nem chega a ser montada.
        if (this._chargedDocumentId === id) return this._loadPrintFrame(id);

        this._documentPdfController.chargePreviewDownload(id).subscribe({
            next: () => {
                this._chargedDocumentId = id;
                this._loadPrintFrame(id);
            },
            error: () => this.preparingPrint.set(false),
        });
    }

    private _loadPrintFrame(id: string): void {
        // A folha vive num iframe ESCONDIDO desta mesma página, e não numa aba nova: o
        // usuário pediu o PDF olhando o documento, e mandá-lo para outra aba (que ele ainda
        // teria de fechar depois de imprimir) troca uma tela por duas.
        //
        // O iframe carrega a rota `pdf-preview`, que já sabe fazer tudo — buscar o documento,
        // cobrar, paginar e chamar a impressão sozinha (`print=1`). `raw=1` para o documento
        // do iframe SER a folha: sem ele a prévia vive num iframe interno e a impressão sairia
        // da página hospedeira, com a casca em volta.
        //
        // A rota TEM de ir depois do `#` (HashLocationStrategy, ver app-config), e a base sai
        // da URL atual e não de PLATFORM_BASE: é a MESMA aplicação, e PLATFORM_BASE aponta
        // para o host publicado — em desenvolvimento saltaria do localhost para a hom.
        const base = window.location.href.split('#')[0];
        const route = this._router.serializeUrl(
            this._router.createUrlTree(['/document', id, 'pdf-preview'], {
                queryParams: {
                    hideMenu: true,
                    raw: 1,
                    // Cada clique é uma impressão nova: sem isto, reimprimir o mesmo
                    // documento manteria o mesmo src e o iframe não recarregaria.
                    t: Date.now(),
                },
            })
        );

        this.printFrameUrl.set(this._sanitizer.bypassSecurityTrustResourceUrl(`${base}#${route}`));

        this._printOverlayTimer = setTimeout(
            () => this.printOverlayOpen.set(true),
            DocumentPreviewPage.PRINT_OVERLAY_DELAY_MS
        );
        this._printTimeoutTimer = setTimeout(
            () => this._onPrintTimeout(),
            DocumentPreviewPage.PRINT_TIMEOUT_MS
        );
    }

    /**
     * Fases da folha que significam "acabou de paginar": só nelas o PDF pode sair. A folha
     * também avisa `start` (antes de paginar) e `error` (um erro qualquer lá dentro, que
     * pode chegar antes mesmo de a paginação começar e ainda ser seguido de `rendered`).
     * Imprimir nessas duas mandaria para o papel a capa com uma folha em branco atrás; o
     * teto de tempo é quem cuida de uma falha de verdade.
     */
    private static readonly PRINTABLE_PHASES = ['rendered', 'partial', 'fallback'];

    /**
     * A folha avisa quando terminou de paginar (o bootstrap do compositor manda a mensagem
     * ao `parent`, que aqui é esta página). É o que apaga o "gerando": a janela de impressão
     * abre logo em seguida, e o navegador já congela a página enquanto ela estiver aberta.
     *
     * Só a mensagem de fase terminal conta (ver PRINTABLE_PHASES): a folha fala mais de uma
     * vez, e cada vez que isto imprimisse abriria OUTRA janela de impressão. As mensagens de
     * ação (mouse sobre um item, por exemplo) também vêm pelo mesmo canal e nunca imprimem.
     */
    private readonly _onPrintFrameMessage = (event: MessageEvent): void => {
        const data = event.data as { source?: string; phase?: string; action?: string } | null;
        if (!data || data.source !== 'rapidocs-pdf-preview' || data.action) return;
        if (!DocumentPreviewPage.PRINTABLE_PHASES.includes(data.phase ?? '')) return;
        // Uma impressão por pedido: depois da janela aberta a folha continua no DOM e
        // qualquer eco dela não pode abrir outra.
        if (!this.preparingPrint()) return;

        this._clearPrintTimers();
        this.preparingPrint.set(false);
        this.printOverlayOpen.set(false);
        this._printFrame();
    };

    /**
     * Manda o IFRAME imprimir, e não esta página. O `focus()` antes é obrigatório: sem ele
     * parte dos navegadores manda para o papel o documento de cima, que aqui é o preview
     * com menu e cabeçalho.
     */
    private _printFrame(): void {
        const win = this._printFrameEl()?.nativeElement?.contentWindow;
        if (!win) return;

        win.focus();
        win.print();
    }

    /**
     * Desiste da espera: tira a folha do ar e devolve a tela ao usuário.
     *
     * NÃO estorna: a cobrança acontece antes de montar as páginas, e quando este botão
     * aparece ela já foi feita. Mas tentar de novo TAMBÉM não cobra outra vez, porque o
     * documento já pago fica lembrado nesta tela (ver `_chargedDocumentId`).
     */
    public cancelPrint(): void {
        // `_chargedDocumentId` fica: é justamente ele que impede a segunda cobrança quando
        // o usuário desiste da espera e tenta de novo.
        this._clearPrintTimers();
        this.preparingPrint.set(false);
        this.printOverlayOpen.set(false);
        this.printFrameUrl.set(null);
    }

    /** Estourou o teto sem a folha avisar nada: libera a tela e diz o que houve. */
    private _onPrintTimeout(): void {
        this._clearPrintTimers();
        this.preparingPrint.set(false);
        this.printOverlayOpen.set(false);
        this.printFrameUrl.set(null);

        this._toastService.error({
            title: this._injector
                .get(TranslateService)
                .instant('document.preview.printingTimeoutTitle'),
            message: this._injector
                .get(TranslateService)
                .instant('document.preview.printingTimeoutMessage'),
        });
    }

    private _clearPrintTimers(): void {
        if (this._printOverlayTimer) clearTimeout(this._printOverlayTimer);
        if (this._printTimeoutTimer) clearTimeout(this._printTimeoutTimer);
        this._printOverlayTimer = null;
        this._printTimeoutTimer = null;
    }

    /**
     * Baixa o PDF já gerado.
     *
     * O arquivo mora no CDN (outra origem), onde o atributo `download` de um `<a>` é
     * ignorado — por isso o download é feito a partir de um `blob:` URL (ver
     * `file-download.util`). Se o arquivo não puder ser buscado (CORS/rede), abre em
     * nova aba: aqui estamos dentro do clique do usuário, então não há popup blocker.
     */
    public downloadPDF(): void {
        const pdfURL = this.document()?.pdfURL;
        if (!pdfURL) {
            this._toastService.error({
                title: this._injector
                    .get(TranslateService)
                    .instant('document.preview.pdfDownloadErrorTitle'),
                message: this._injector
                    .get(TranslateService)
                    .instant('document.preview.pdfDownloadErrorMessage'),
            });

            return;
        }

        const fileName = pdfFileNameFromTitle(this.document()?.title, pdfURL);

        void downloadFileFromUrl(pdfURL, fileName).then(downloaded => {
            if (!downloaded) openFileInNewTab(pdfURL);
        });
    }

    //#endregion

    //#region [Common methods]

    private _checkIfShowMenu(): void {
        const urlParamsHideMenu = this._activateRoute.snapshot.queryParams['hideMenu'];
        this.hideMenu.set(urlParamsHideMenu === 'true');
    }

    public previewFontFamily(): string {
        // Prioridade: documento > company > default
        const docFont = this.document()?.config?.fontFamily;
        if (docFont && typeof docFont === 'string' && docFont.trim()) {
            return docFont.trim();
        }

        const companyFont = this.document()?.company?.documentConfig?.configs?.fontFamily;
        if (companyFont && typeof companyFont === 'string' && companyFont.trim()) {
            return companyFont.trim();
        }

        return environment.DEFAULT_FONT_FAMILY;
    }

    public showSignatureSection(): boolean {
        return (
            this.document()?.type === DocumentTypeEnum.CONTRACT &&
            this.document()?.status === DocumentStatusEnum.PUBLISHED
        );
    }

    //#endregion
}
